/**
 * Correlated color temperature (CCT, kelvin) -> linear sRGB tint.
 *
 * Implementation steps:
 *   1. Map CCT to CIE 1960 UCS (u, v) via Kim et al. 2002's rational polynomial
 *      fit to the Planckian locus. Valid 1000K..15000K; domain-clamped.
 *   2. Convert (u, v) -> xy -> XYZ (Y = 1).
 *   3. Apply the standard sRGB (D65) matrix to get linear sRGB.
 *   4. Renormalize so that 6500K lands exactly at (1, 1, 1). Without this step
 *      the Planckian locus at "6500K" sits slightly green of the D65 white
 *      point because the exact CCT of D65 is ~6504K and the fit has its own
 *      residuals; anchoring fixes it so users see a true neutral at 6500K.
 *   5. Luminance-normalize (Rec.709) so swapping temperature only shifts hue,
 *      not brightness - the lamp's candela output remains the sole knob for
 *      brightness.
 *
 * Note: 4000K is physically a warm light relative to a D65 display. LED
 * marketing that labels 4000K as "neutral" / "daylight" is describing
 * chromatically-adapted perception (your eyes adjust), not the color you see
 * on a calibrated monitor.
 */

type RGB = [number, number, number];

export function kelvinToLinearRgb(kelvin: number): RGB {
  const raw = cctToLinearSrgb(kelvin);
  // Anchor D65 -> (1,1,1) so the slider at 6500K is genuinely neutral.
  const rel: RGB = [raw[0] / D65_LINEAR[0], raw[1] / D65_LINEAR[1], raw[2] / D65_LINEAR[2]];

  const y = 0.2126 * rel[0] + 0.7152 * rel[1] + 0.0722 * rel[2];
  if (y <= 1e-6) return [0, 0, 0];
  return [rel[0] / y, rel[1] / y, rel[2] / y];
}

/**
 * Display-friendly CSS hex for a given Kelvin value: peak channel is mapped
 * to 1.0 (so the swatch is vivid rather than pitch black at 2000K) and then
 * gamma-encoded with the real sRGB transfer function.
 */
export function kelvinToCssHex(kelvin: number): string {
  const lin = cctToLinearSrgb(kelvin);
  const m = Math.max(lin[0], lin[1], lin[2], 1e-6);
  const r = linearToSrgb(lin[0] / m);
  const g = linearToSrgb(lin[1] / m);
  const b = linearToSrgb(lin[2] / m);
  const toByte = (c: number) =>
    Math.round(Math.max(0, Math.min(1, c)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
}

// Raw linear sRGB for the D65 (6500K) reference used to anchor the slider.
const D65_LINEAR = cctToLinearSrgbRaw(6500);

function cctToLinearSrgb(K: number): RGB {
  const raw = cctToLinearSrgbRaw(K);
  return [Math.max(0, raw[0]), Math.max(0, raw[1]), Math.max(0, raw[2])];
}

/**
 * Un-clamped CIE 1960 UCS -> linear sRGB. Negative components can occur for
 * CCTs whose chromaticity falls outside the sRGB gamut; callers clamp as
 * appropriate (zero for emitted color, free for the D65 anchor where
 * small negative residuals would poison the division).
 */
function cctToLinearSrgbRaw(K: number): RGB {
  const T = Math.max(1000, Math.min(15000, K));
  const T2 = T * T;
  const u = (0.860117757 + 1.54118254e-4 * T + 1.28641212e-7 * T2) / (1 + 8.42420235e-4 * T + 7.08145163e-7 * T2);
  const v = (0.317398726 + 4.22806245e-5 * T + 4.20481691e-8 * T2) / (1 - 2.89741816e-5 * T + 1.61456053e-7 * T2);

  const d = 2 * u - 8 * v + 4;
  const x = (3 * u) / d;
  const y = (2 * v) / d;

  // XYZ with Y = 1.
  const X = x / y;
  const Z = (1 - x - y) / y;

  // sRGB (D65) matrix.
  const R = 3.2406 * X - 1.5372 - 0.4986 * Z;
  const G = -0.9689 * X + 1.8758 + 0.0415 * Z;
  const B = 0.0557 * X - 0.204 + 1.057 * Z;
  return [R, G, B];
}

function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}
